import axios, { AxiosError } from 'axios'
import type { AxiosInstance } from 'axios'
import type {
  User,
  Contact,
  Company,
  Project,
  ProjectStats,
  Deal,
  DealScoreResponse,
  DealNextActionResponse,
  DealAlertsResponse,
  DealTimelineResponse,
  DealCalendarResponse,
  ToolRegistryItem,
  AISkillSummary,
  AISkillDetail,
  KanbanView,
  Pipeline,
  Task,
  Email,
  Conversation,
  Message,
  ChatResponse,
  CreateContactRequest,
  CreateDealRequest,
  CreateTaskRequest,
  PaginatedResponse,
  PMTask,
  PMTaskState,
  CreatePMTaskRequest,
  BulkOperationResponse,
  TaskTimeEntry,
  CreateTaskTimeEntryRequest,
  WorkflowState,
  EstimateTemplate,
  EstimatePoint,
  Cycle,
  CreateCycleRequest,
  UpdateCycleRequest,
  TaskComment,
  CreateTaskCommentRequest,
  TaskLink,
  CreateTaskLinkRequest,
  TaskAttachment,
  CreateTaskAttachmentRequest,
  TaskWatcher,
  TaskWatchStatus,
  TaskWatchSettingsRequest,
  TaskActivityEvent,
  TaskRelationship,
  SupportTicket,
  CreateTicketRequest,
  UpdateTicketRequest,
  AddTicketMessageRequest,
  SupportTicketMessage,
  TicketStats,
  TicketCategory,
  ApiResponse,
  MessagingAccount,
  MessagingLocation,
  MessagingConversation,
  MessagingMessage,
  FollowUpSuggestion,
  SendMessageRequest,
  SendMessageResponse,
  MessagingAccountStatus,
  SupportAdminTicketSummary,
  SupportAdminTicketDetail,
  SupportAdminStats,
  SupportAdminFilters,
  ActivityItem,
  CalendarEvent,
  CreateCalendarEventInput,
  ContactInsight,
  ContactTimelineItem,
  ContactFile,
  ThreadSummary,
  DraftReply,
  DailyBriefing,
  InboxItem,
  Action,
  ActionStatus,
  CreateActionRequest,
  BmcpProblem,
  BmcpProblemsListResponse,
  BmcpProblemPriority,
  BmcpProblemStatus,
  VerifyRun,
  VerifyRunsListResponse,
  VerifyFindingsListResponse,
  TriggerVerifyRequest,
  TriggerVerifyResponse,
  FindingSeverity,
  FindingType,
} from '@/types/api'

export function resolveApiBaseUrl(
  envBaseUrl?: string,
  runtimeOrigin?: string
): string {
  const env = (envBaseUrl ?? '').trim()
  const runtime = (runtimeOrigin ?? '').trim()

  // Defensive guard: avoid accidentally shipping a dev API base URL baked into
  // the production build (e.g. VITE_API_BASE_URL=http://localhost:8080).
  // In that case prefer the runtime origin (same host reverse proxy).
  if (env) {
    if (runtime && !isLocalhostOrigin(runtime) && isLocalhostBaseUrl(env)) {
      return runtime
    }
    return env
  }

  if (runtime) return runtime
  return ''
}

function normalizeHostname(hostname: string): string {
  return hostname.trim().replace(/^\[/, '').replace(/\]$/, '').toLowerCase()
}

function isLocalhostHostname(hostname: string): boolean {
  const value = normalizeHostname(hostname)
  return (
    value === 'localhost' ||
    value === '127.0.0.1' ||
    value === '0.0.0.0' ||
    value === '::1'
  )
}

function isLocalhostOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    return isLocalhostHostname(url.hostname)
  } catch {
    return false
  }
}

function isLocalhostBaseUrl(baseUrl: string): boolean {
  const value = baseUrl.trim()
  if (!value) return false
  if (value.startsWith('/')) return false

  // Support:
  // - http://localhost:8080
  // - http://127.0.0.1:8080
  // - localhost:8080 (scheme-less; still a dev URL)
  // - http://[::1]:8080
  const match = /^(?:https?:\/\/)?([^/]+)(?:\/|$)/i.exec(value)
  if (!match) return false

  const host = match[1] || ''
  const hostname = host.startsWith('[')
    ? host.split(']')[0] + ']'
    : host.split(':')[0]
  return isLocalhostHostname(hostname)
}

const API_BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL,
  typeof window !== 'undefined' ? window.location.origin : ''
)
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1'
const SAAS_ADMIN_BASE_URL = (import.meta.env.VITE_SAAS_ADMIN_BASE_URL ?? API_BASE_URL ?? '')
  .toString()
  .replace(/\/+$/, '')

export function buildApiBaseURL(apiBaseUrl: string, apiVersion: string): string {
  const base = (apiBaseUrl || '').replace(/\/+$/, '')
  const version = (apiVersion || 'v1').replace(/^\/+/, '')

  // Accept envs like:
  // - http://localhost:8080  -> http://localhost:8080/api/v1
  // - https://runrate.me     -> https://runrate.me/api/v1
  // - /api                   -> /api/v1 (nginx proxies /api/* to backend)
  // - https://runrate.me/api -> https://runrate.me/api/v1
  // - https://runrate.me/api/v1 -> https://runrate.me/api/v1
  if (!base) return `/api/${version}`
  if (base.endsWith(`/api/${version}`)) return base
  if (/\/api\/v\d+$/i.test(base)) return base
  if (base.endsWith('/api')) return `${base}/${version}`
  return `${base}/api/${version}`
}

// Create axios instance
const ACTIVE_ORGANIZATION_STORAGE_KEY = 'go-crm:workspace:id'

type OrganizationAwareResponse = {
  organization_id?: string | null
}

export function getActiveOrganizationId(): string | null {
  return localStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY)
}

export function setActiveOrganizationId(organizationId?: string | null) {
  const normalized = organizationId?.trim()
  if (!normalized) return
  // Keep the legacy storage key so existing clients do not lose auth context.
  localStorage.setItem(ACTIVE_ORGANIZATION_STORAGE_KEY, normalized)
}

export function clearActiveOrganizationId() {
  localStorage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY)
}

function syncActiveOrganizationSelection(organizationId?: string | null) {
  if (!organizationId) return
  setActiveOrganizationId(organizationId)
}

const PUBLIC_AUTH_PATH_PREFIXES = ['/login', '/auth/verify', '/invite', '/book']
const AUTH_PROBE_PATHS = ['/me']

function normalizeRequestPath(requestUrl: string): string {
  if (!requestUrl) return ''

  try {
    const parsed = new URL(requestUrl, 'https://runrate.local')
    return parsed.pathname.replace(/^\/api\/v\d+(?=\/|$)/i, '') || '/'
  } catch {
    return requestUrl.split('?')[0].replace(/^\/api\/v\d+(?=\/|$)/i, '') || '/'
  }
}

function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function shouldRedirectToLoginOnUnauthorized(
  requestUrl: string,
  currentPathname = typeof window !== 'undefined' ? window.location.pathname : ''
): boolean {
  const requestPath = normalizeRequestPath(requestUrl)

  // /me is the session probe used on public and protected routes. Let the
  // caller update auth state; ProtectedRoute handles protected navigation.
  if (AUTH_PROBE_PATHS.includes(requestPath)) return false

  // Auth verification failures and public auth pages must render their own UI,
  // not trigger a full-page reload back to /login.
  if (requestPath === '/auth/verify' || isPublicAuthPath(currentPathname)) return false

  return currentPathname !== '/login'
}

function attachAuthInterceptors(client: AxiosInstance) {
  // Auth token is sent as httpOnly cookie automatically — no localStorage needed.
  client.defaults.withCredentials = true

  client.interceptors.request.use(
    (config) => {
      const organizationId = getActiveOrganizationId()
      if (organizationId) {
        config.headers['X-Organization-ID'] = organizationId
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || ''
        // Unauthorized — cookie is httpOnly so nothing to clear in JS.
        clearActiveOrganizationId()
        if (shouldRedirectToLoginOnUnauthorized(requestUrl)) {
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }
  )
}

export const apiClient = axios.create({
  baseURL: buildApiBaseURL(API_BASE_URL, API_VERSION),
  headers: {
    'Content-Type': 'application/json',
  },
})

export const saasAdminApiClient = axios.create({
  baseURL: SAAS_ADMIN_BASE_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
attachAuthInterceptors(apiClient)
attachAuthInterceptors(saasAdminApiClient)

// Auth API — magic-link only; password login has been removed.
export const authApi = {
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout')
    // Cookie is cleared server-side; only org selection is in localStorage.
    clearActiveOrganizationId()
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User & OrganizationAwareResponse>('/me')
    syncActiveOrganizationSelection(response.data.organization_id)
    return response.data
  },

  // Magic Link authentication
  requestMagicLink: async (
    email: string,
    redirect?: string
  ): Promise<{ success: boolean; message: string; request_id?: string }> => {
    const payload: { email: string; redirect?: string } = { email }
    if (redirect) {
      payload.redirect = redirect
    }
    const response = await apiClient.post('/auth/magic-link', payload)
    return response.data
  },
}

// Contacts API
export const contactsApi = {
  list: async (params?: {
    page?: number
    page_size?: number
    search?: string
    project_id?: string
  }): Promise<PaginatedResponse<Contact>> => {
    const response = await apiClient.get<PaginatedResponse<Contact>>('/contacts', {
      params,
    })
    return response.data
  },

  get: async (id: string): Promise<Contact> => {
    const response = await apiClient.get<Contact>(`/contacts/${id}`)
    return response.data
  },

  create: async (data: CreateContactRequest): Promise<Contact> => {
    const response = await apiClient.post<Contact>('/contacts', data)
    return response.data
  },

  update: async (id: string, data: Partial<CreateContactRequest>): Promise<Contact> => {
    const response = await apiClient.put<Contact>(`/contacts/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/contacts/${id}`)
  },

  getInsights: async (id: string): Promise<ContactInsight> => {
    const response = await apiClient.get<ContactInsight>(`/contacts/${id}/insights`)
    return response.data
  },

  getTimeline: async (id: string): Promise<ContactTimelineItem[]> => {
    const response = await apiClient.get<ContactTimelineItem[]>(`/contacts/${id}/timeline`)
    return response.data
  },

  getFiles: async (id: string): Promise<ContactFile[]> => {
    const response = await apiClient.get<ContactFile[]>(`/contacts/${id}/files`)
    return response.data
  },
}

// Companies API
export const companiesApi = {
  list: async (params?: {
    page?: number
    page_size?: number
    search?: string
    project_id?: string
  }): Promise<PaginatedResponse<Company>> => {
    const response = await apiClient.get<PaginatedResponse<Company>>('/companies', {
      params,
    })
    return response.data
  },

  get: async (id: string): Promise<Company> => {
    const response = await apiClient.get<Company>(`/companies/${id}`)
    return response.data
  },

  create: async (data: Partial<Company>): Promise<Company> => {
    const response = await apiClient.post<Company>('/companies', data)
    return response.data
  },

  update: async (id: string, data: Partial<Company>): Promise<Company> => {
    const response = await apiClient.put<Company>(`/companies/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/companies/${id}`)
  },
}

// Projects API
export const projectsApi = {
  list: async (params?: {
    page?: number
    page_size?: number
    status?: string
  }): Promise<PaginatedResponse<Project>> => {
    const response = await apiClient.get<PaginatedResponse<Project>>('/projects', { params })
    return response.data
  },

  get: async (id: string): Promise<Project> => {
    const response = await apiClient.get<Project>(`/projects/${id}`)
    return response.data
  },

  create: async (data: {
    name: string
    slug: string
    description?: string
    icon?: string
    color?: string
    settings?: Record<string, unknown>
  }): Promise<Project> => {
    const response = await apiClient.post<Project>('/projects', data)
    return response.data
  },

  update: async (id: string, data: Partial<{
    name: string
    slug: string
    description: string
    icon: string
    color: string
    status: string
    is_favorite: boolean
    settings: Record<string, unknown>
  }>): Promise<Project> => {
    const response = await apiClient.put<Project>(`/projects/${id}`, data)
    return response.data
  },

  archive: async (id: string): Promise<void> => {
    await apiClient.post(`/projects/${id}/archive`)
  },

  unarchive: async (id: string): Promise<void> => {
    await apiClient.post(`/projects/${id}/unarchive`)
  },

  toggleFavorite: async (id: string): Promise<Project> => {
    const response = await apiClient.post<Project>(`/projects/${id}/favorite`)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/projects/${id}`)
  },

  getStats: async (id: string): Promise<ProjectStats> => {
    const response = await apiClient.get<ProjectStats>(`/projects/${id}/stats`)
    return response.data
  },
}

// BMCP Problems API (verify findings -> problems)
export const bmcpProblemsApi = {
  list: async (params: {
    project_id?: string
    task_id?: string
    status?: BmcpProblemStatus
    limit?: number
    offset?: number
  }): Promise<BmcpProblemsListResponse> => {
    const response = await apiClient.get<BmcpProblemsListResponse>('/problems', { params })
    return response.data
  },

  patch: async (
    id: string,
    data: {
      status?: BmcpProblemStatus
      priority?: BmcpProblemPriority
      dismissed?: boolean
    }
  ): Promise<BmcpProblem> => {
    const response = await apiClient.patch<BmcpProblem>(`/problems/${id}`, data)
    return response.data
  },
}

// BMCP Verify API
export const verifyApi = {
  listRunsByTask: async (
    taskId: string,
    limit = 20,
    offset = 0
  ): Promise<VerifyRunsListResponse> => {
    const response = await apiClient.get<VerifyRunsListResponse>(
      `/verify/tasks/${taskId}/runs`,
      { params: { limit, offset } }
    )
    return response.data
  },

  getRun: async (runId: string): Promise<VerifyRun> => {
    const response = await apiClient.get<VerifyRun>(`/verify/runs/${runId}`)
    return response.data
  },

  listFindings: async (
    runId: string,
    filters?: { severity?: FindingSeverity; type?: FindingType }
  ): Promise<VerifyFindingsListResponse> => {
    const response = await apiClient.get<VerifyFindingsListResponse>(
      `/verify/runs/${runId}/findings`,
      { params: filters }
    )
    return response.data
  },

  triggerVerify: async (
    taskId: string,
    req?: TriggerVerifyRequest
  ): Promise<TriggerVerifyResponse> => {
    const response = await apiClient.post<TriggerVerifyResponse>(
      `/verify/tasks/${taskId}/verify`,
      req ?? {}
    )
    return response.data
  },
}

// Deals API
export const dealsApi = {
  list: async (params?: {
    pipeline_id?: string
    stage_id?: string
    status?: string
    page?: number
    page_size?: number
    project_id?: string
  }): Promise<PaginatedResponse<Deal>> => {
    const response = await apiClient.get<PaginatedResponse<Deal>>('/deals', {
      params,
    })
    return response.data
  },

  kanban: async (params: {
    pipeline_id: string
    owner_id?: string
    min_value?: number
    max_value?: number
    order_by?: string
    order_dir?: string
    tags?: string[]
    project_id?: string
  }): Promise<KanbanView> => {
    const searchParams = new URLSearchParams()
    searchParams.set('pipeline_id', params.pipeline_id)
    if (params.owner_id) searchParams.set('owner_id', params.owner_id)
    if (typeof params.min_value === 'number') searchParams.set('min_value', params.min_value.toString())
    if (typeof params.max_value === 'number') searchParams.set('max_value', params.max_value.toString())
    if (params.order_by) searchParams.set('order_by', params.order_by)
    if (params.order_dir) searchParams.set('order_dir', params.order_dir)
    if (params.project_id) searchParams.set('project_id', params.project_id)
    if (params.tags?.length) {
      params.tags.forEach((tag) => searchParams.append('tags', tag))
    }

    const response = await apiClient.get<ApiResponse<KanbanView>>('/deals/kanban', {
      params: searchParams,
    })
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load kanban view')
    }
    return response.data.data
  },

  timeline: async (params: {
    pipeline_id: string
    start_date: string
    end_date: string
  }): Promise<DealTimelineResponse> => {
    const searchParams = new URLSearchParams()
    searchParams.set('pipeline_id', params.pipeline_id)
    searchParams.set('start_date', params.start_date)
    searchParams.set('end_date', params.end_date)

    const response = await apiClient.get<ApiResponse<DealTimelineResponse>>('/deals/timeline', {
      params: searchParams,
    })
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load timeline view')
    }
    return response.data.data
  },

  alerts: async (params?: {
    pipeline_id?: string
    owner_id?: string
    severity?: string
    limit?: number
  }): Promise<DealAlertsResponse> => {
    const response = await apiClient.get<ApiResponse<DealAlertsResponse>>('/deals/alerts', { params })
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load deal alerts')
    }
    return response.data.data
  },

  dismissAlert: async (id: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<null>>(`/deals/alerts/${id}/dismiss`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to dismiss deal alert')
    }
  },

  calendar: async (params: {
    pipeline_id: string
    month: number
    year: number
  }): Promise<DealCalendarResponse> => {
    const response = await apiClient.get<ApiResponse<DealCalendarResponse>>('/deals/calendar', {
      params,
    })
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load calendar view')
    }
    return response.data.data
  },

  get: async (id: string): Promise<Deal> => {
    const response = await apiClient.get<Deal>(`/deals/${id}`)
    return response.data
  },

  getScore: async (id: string): Promise<DealScoreResponse> => {
    const response = await apiClient.get<ApiResponse<DealScoreResponse>>(`/deals/${id}/score`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load deal score')
    }
    return response.data.data
  },

  getNextAction: async (id: string): Promise<DealNextActionResponse> => {
    const response = await apiClient.get<ApiResponse<DealNextActionResponse>>(`/deals/${id}/next-action`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load next action')
    }
    return response.data.data
  },

  create: async (data: CreateDealRequest): Promise<Deal> => {
    const response = await apiClient.post<Deal>('/deals', data)
    return response.data
  },

  update: async (id: string, data: Partial<CreateDealRequest>): Promise<Deal> => {
    const response = await apiClient.put<Deal>(`/deals/${id}`, data)
    return response.data
  },

  moveStage: async (id: string, stage_id: string, position?: number): Promise<Deal> => {
    const response = await apiClient.post<Deal>(`/deals/${id}/move`, {
      stage_id,
      position,
    })
    return response.data
  },

  markWon: async (id: string): Promise<Deal> => {
    const response = await apiClient.post<Deal>(`/deals/${id}/won`)
    return response.data
  },

  markLost: async (id: string, reason?: string): Promise<Deal> => {
    const response = await apiClient.post<Deal>(`/deals/${id}/lost`, {
      reason,
    })
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/deals/${id}`)
  },
}

// Pipelines API
export const pipelinesApi = {
  list: async (): Promise<Pipeline[]> => {
    const response = await apiClient.get<Pipeline[] | ApiResponse<Pipeline[]>>('/pipelines')
    const body = response.data
    if (Array.isArray(body)) return body
    return body?.data ?? []
  },

  get: async (id: string): Promise<Pipeline> => {
    const response = await apiClient.get<Pipeline | ApiResponse<Pipeline>>(`/pipelines/${id}`)
    const body = response.data
    return (body && typeof body === 'object' && 'data' in body ? (body as ApiResponse<Pipeline>).data : body) as Pipeline
  },

  getDefault: async (): Promise<Pipeline> => {
    const response = await apiClient.get<Pipeline | ApiResponse<Pipeline>>('/pipelines/default')
    const body = response.data
    return (body && typeof body === 'object' && 'data' in body ? (body as ApiResponse<Pipeline>).data : body) as Pipeline
  },
}

// Tasks API
export const tasksApi = {
  list: async (params?: {
    status?: string
    priority?: string
    assignee_id?: string
    due_date_from?: string
    due_date_to?: string
    page?: number
    page_size?: number
    project_id?: string
  }): Promise<PaginatedResponse<Task>> => {
    const response = await apiClient.get<PaginatedResponse<Task>>('/tasks', {
      params,
    })
    return response.data
  },

  get: async (id: string): Promise<Task> => {
    const response = await apiClient.get<Task>(`/tasks/${id}`)
    return response.data
  },

  create: async (data: CreateTaskRequest): Promise<Task> => {
    const response = await apiClient.post<Task>('/tasks', data)
    return response.data
  },

  update: async (id: string, data: Partial<CreateTaskRequest>): Promise<Task> => {
    const response = await apiClient.put<Task>(`/tasks/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tasks/${id}`)
  },
}

// Actions API (Daily Actions / Suggested Actions)
export const actionsApi = {
  list: async (params?: {
    status?: string // 'todo' | 'in_progress' | 'done' | 'all'
    source?: string // 'email' | 'whatsapp' | 'telegram' | 'manual'
    priority?: string // 'high' | 'medium' | 'low'
    project_id?: string
  }): Promise<{ success: boolean; actions: Action[]; count: number }> => {
    const response = await apiClient.get<{ success: boolean; actions: Action[]; count: number }>('/actions', { params })
    return response.data
  },

  create: async (data: CreateActionRequest): Promise<{ success: boolean; action: Action }> => {
    const response = await apiClient.post<{ success: boolean; action: Action }>('/actions', data)
    return response.data
  },

  updateStatus: async (id: string, status: ActionStatus): Promise<{ success: boolean; action: Action }> => {
    const response = await apiClient.post<{ success: boolean; action: Action }>(`/actions/${id}/status`, { status })
    return response.data
  },

  complete: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/actions/${id}/complete`)
    return response.data
  },

  dismiss: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/actions/${id}/dismiss`)
    return response.data
  },

  snooze: async (id: string, until: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/actions/${id}/snooze`, { until })
    return response.data
  },

  getDailyActions: async (): Promise<{ success: boolean; actions: Action[]; count: number; date: string }> => {
    const response = await apiClient.get<{ success: boolean; actions: Action[]; count: number; date: string }>('/actions/daily')
    return response.data
  },
}

// Emails API
export const emailsApi = {
  list: async (params?: {
    folder?: string
    is_read?: boolean
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<Email>> => {
    const response = await apiClient.get<PaginatedResponse<Email>>('/emails', {
      params,
    })
    return response.data
  },

  get: async (id: string): Promise<Email> => {
    const response = await apiClient.get<Email>(`/emails/${id}`)
    return response.data
  },

  markRead: async (id: string): Promise<void> => {
    await apiClient.post(`/emails/${id}/read`)
  },

  markUnread: async (id: string): Promise<void> => {
    await apiClient.post(`/emails/${id}/unread`)
  },

  archive: async (id: string): Promise<void> => {
    await apiClient.post(`/emails/${id}/archive`)
  },

  trash: async (id: string): Promise<void> => {
    await apiClient.post(`/emails/${id}/trash`)
  },

  reply: async (id: string, payload: { body: string; subject?: string; to?: string }): Promise<{ success: boolean; message_id?: string }> => {
    const response = await apiClient.post<{ success: boolean; message_id?: string }>(
      `/emails/${id}/reply`,
      payload
    )
    return response.data
  },
}

// AI Assistant API
export const assistantApi = {
  chat: async (
    message: string,
    conversation_id?: string,
    skill_slug?: string
  ): Promise<ChatResponse> => {
    const response = await apiClient.post('/assistant/chat', {
      message,
      conversation_id,
      skill_slug,
    })
    return response.data
  },

  chatStream: async (params: {
    message: string
    conversation_id?: string
    skill_slug?: string
    signal?: AbortSignal
    onDelta?: (content: string) => void
  }): Promise<ChatResponse> => {
    const { message, conversation_id, skill_slug, signal, onDelta } = params
    const baseURL = buildApiBaseURL(API_BASE_URL, API_VERSION)
    const url = `${baseURL}/assistant/chat/stream`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const organizationId = getActiveOrganizationId()
    if (organizationId) {
      headers['X-Organization-ID'] = organizationId
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include', // send httpOnly auth cookie
      body: JSON.stringify({
        message,
        conversation_id,
        skill_slug,
      }),
      signal,
    })

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '')
      throw new Error(text || 'Failed to stream assistant response')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let finalResponse: ChatResponse | null = null

    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let boundaryIndex = buffer.indexOf('\n\n')
      while (boundaryIndex !== -1) {
        const rawEvent = buffer.slice(0, boundaryIndex)
        buffer = buffer.slice(boundaryIndex + 2)
        boundaryIndex = buffer.indexOf('\n\n')

        const lines = rawEvent.split('\n')
        let eventName = 'message'
        const dataLines: string[] = []
        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventName = line.replace('event:', '').trim()
          } else if (line.startsWith('data:')) {
            dataLines.push(line.replace('data:', '').trim())
          }
        }
        if (dataLines.length === 0) {
          continue
        }
        const dataRaw = dataLines.join('\n')
        let payload: unknown = dataRaw
        try {
          payload = JSON.parse(dataRaw)
        } catch {
          // keep raw string
        }

        if (eventName === 'delta') {
          const content =
            typeof payload === 'string'
              ? payload
              : payload &&
                  typeof payload === 'object' &&
                  'content' in payload &&
                  typeof payload.content === 'string'
                ? payload.content
                : undefined
          if (content) onDelta?.(content)
        } else if (eventName === 'done') {
          if (payload && typeof payload === 'object') {
            finalResponse = payload as ChatResponse
          }
        } else if (eventName === 'error') {
          const errMsg =
            typeof payload === 'string'
              ? payload
              : payload &&
                  typeof payload === 'object' &&
                  'error' in payload &&
                  typeof payload.error === 'string'
                ? payload.error
                : undefined
          throw new Error(errMsg || 'Assistant stream error')
        }
      }
    }

    if (!finalResponse) {
      throw new Error('Assistant stream ended without final response')
    }
    return finalResponse
  },

  getConversations: async (): Promise<Conversation[]> => {
    const response = await apiClient.get<Conversation[]>('/assistant/conversations')
    return response.data
  },

  getConversation: async (id: string): Promise<Conversation & { messages: Message[] }> => {
    const response = await apiClient.get(`/assistant/conversations/${id}`)
    return response.data
  },

  summarizeThread: async (payload: {
    thread_id: string
    thread_type: 'email' | 'conversation'
  }): Promise<ThreadSummary> => {
    const response = await apiClient.post<ThreadSummary>('/assistant/threads/summarize', payload)
    return response.data
  },

  draftReply: async (payload: {
    thread_id: string
    thread_type: 'email' | 'conversation'
    prompt?: string
  }): Promise<DraftReply> => {
    const response = await apiClient.post<DraftReply>('/assistant/threads/draft', payload)
    return response.data
  },

  getContactInsights: async (contactId: string): Promise<ContactInsight> => {
    const response = await apiClient.get<ContactInsight>(`/assistant/contacts/${contactId}/insights`)
    return response.data
  },

  getDailyBriefing: async (date?: string): Promise<DailyBriefing> => {
    const response = await apiClient.get<DailyBriefing>('/assistant/briefing/daily', {
      params: date ? { date } : undefined,
    })
    return response.data
  },

  listToolRegistry: async (): Promise<{ tools: ToolRegistryItem[] }> => {
    const response = await apiClient.get<ApiResponse<{ tools: ToolRegistryItem[] }>>('/assistant/tool-registry')
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load tool registry')
    }
    return response.data.data
  },

  listSkills: async (): Promise<{ skills: AISkillSummary[] }> => {
    const response = await apiClient.get<ApiResponse<{ skills: AISkillSummary[] }>>('/assistant/skills')
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load skills')
    }
    return response.data.data
  },

  getSkill: async (slug: string): Promise<AISkillDetail> => {
    const response = await apiClient.get<ApiResponse<AISkillDetail>>(`/assistant/skills/${slug}`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load skill')
    }
    return response.data.data
  },

  setToolEnabled: async (name: string, enabled: boolean): Promise<{ name: string; enabled: boolean }> => {
    const response = await apiClient.put<ApiResponse<{ name: string; enabled: boolean }>>(`/assistant/tool-registry/${name}`, {
      enabled,
    })
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update tool')
    }
    return response.data.data
  },
}

// Follow-ups API
export const followupsApi = {
  listPending: async (limit: number = 20): Promise<FollowUpSuggestion[]> => {
    const response = await apiClient.get<{ success: boolean; suggestions: FollowUpSuggestion[] }>(
      '/followups/pending',
      { params: { limit } }
    )
    return response.data.suggestions ?? []
  },
}

// Activity API
export const activityApi = {
  list: async (params?: {
    limit?: number
    since?: string
  }): Promise<ActivityItem[]> => {
    const response = await apiClient.get<ActivityItem[]>('/activity', { params })
    return response.data
  },
}

// Calendar API
export const calendarApi = {
  listEvents: async (params: {
    start: string
    end: string
  }): Promise<CalendarEvent[]> => {
    const response = await apiClient.get<CalendarEvent[]>('/calendar/events', { params })
    return response.data
  },

  getTodayEvents: async (): Promise<CalendarEvent[]> => {
    const response = await apiClient.get<CalendarEvent[]>('/calendar/events/today')
    return response.data
  },

  getEvent: async (id: string): Promise<CalendarEvent> => {
    const response = await apiClient.get<CalendarEvent>(`/calendar/events/${id}`)
    return response.data
  },

  createEvent: async (data: CreateCalendarEventInput): Promise<CalendarEvent> => {
    const response = await apiClient.post<CalendarEvent>('/calendar/events', data)
    return response.data
  },

  updateEvent: async (id: string, data: Partial<CreateCalendarEventInput>): Promise<CalendarEvent> => {
    const response = await apiClient.put<CalendarEvent>(`/calendar/events/${id}`, data)
    return response.data
  },

  deleteEvent: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/calendar/events/${id}`)
    return response.data
  },

  createFromEmail: async (data: {
    email_id: string
    summary: string
    start_time: string
    duration_minutes: number
    calendar_id?: string
  }): Promise<{ success: boolean; event?: CalendarEvent }> => {
    const response = await apiClient.post<{ success: boolean; event?: CalendarEvent }>(
      '/calendar/create-from-email',
      data
    )
    return response.data
  },
}

// Unified Inbox API
export const inboxApi = {
  listItems: async (params?: {
    folder?: string
    limit?: number
    offset?: number
    project_id?: string
  }): Promise<InboxItem[]> => {
    const response = await apiClient.get<InboxItem[]>('/inbox/items', { params })
    return response.data
  },
  archiveItem: async (id: string, payload: { type: string }): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/inbox/items/${id}/archive`, payload)
    return response.data
  },
  trashItem: async (id: string, payload: { type: string }): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/inbox/items/${id}/trash`, payload)
    return response.data
  },
  deleteItem: async (id: string, payload: { type: string }): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/inbox/items/${id}/delete`, payload)
    return response.data
  },
}

// Integrations API
export interface IntegrationProvider {
  connected: boolean
  account_id?: string
  email?: string
}

export interface IntegrationsStatus {
  providers: {
    google?: IntegrationProvider
    microsoft?: IntegrationProvider
    linkedin?: IntegrationProvider
    telegram?: IntegrationProvider
    whatsapp?: IntegrationProvider
  }
}

export const integrationsApi = {
  getStatus: async (): Promise<IntegrationsStatus> => {
    const response = await apiClient.get<IntegrationsStatus>('/integrations/status')
    return response.data
  },
}

// Dashboard/Stats API
export interface RecentActivity {
  id: string
  type: string
  description: string
  created_at: string
}

export interface DashboardStats {
  total_contacts: number
  total_companies: number
  total_deals: number
  total_revenue: number
  open_tasks: number
  recent_activities: RecentActivity[]
}

export interface PipelineStats {
  pipeline_id: string
  total_deals: number
  total_value: number
  stages: { stage_id: string; count: number; value: number }[]
}

export const statsApi = {
  getDashboard: async (): Promise<DashboardStats> => {
    const response = await apiClient.get('/stats/dashboard')
    return response.data
  },

  getPipelineStats: async (pipeline_id: string): Promise<PipelineStats> => {
    const response = await apiClient.get(`/stats/pipeline/${pipeline_id}`)
    return response.data
  },
}

// PM Tasks API (Kanban board)
export const pmTasksApi = {
  list: async (params?: {
    project_id?: string
    state?: PMTaskState
    state_id?: string
    priority?: string
    assignee_id?: string
    page?: number
    page_size?: number
    limit?: number
    offset?: number
    external_source?: string
    external_id?: string
  }): Promise<PaginatedResponse<PMTask>> => {
    const pageSize = params?.page_size ?? params?.limit ?? 50
    const page = params?.page ?? 1
    const offset = params?.offset ?? Math.max(0, (page - 1) * pageSize)
    const apiParams = {
      project_id: params?.project_id,
      limit: pageSize,
      offset,
      priority: params?.priority,
      assignee_id: params?.assignee_id,
      state_id: params?.state_id,
      external_source: params?.external_source,
      external_id: params?.external_id,
    }

    const response = await apiClient.get('/pm/tasks', { params: apiParams })
    const data = response.data as {
      items?: PMTask[]
      tasks?: PMTask[]
      total?: number
      count?: number
      page?: number
      page_size?: number
      has_more?: boolean
    }

    if (Array.isArray(data?.items)) {
      return data as PaginatedResponse<PMTask>
    }

    const items = data?.tasks ?? []
    const total = data?.total ?? data?.count ?? items.length
    return {
      items,
      total,
      page,
      page_size: pageSize,
      has_more: offset + pageSize < total,
    }
  },

  get: async (id: string): Promise<PMTask> => {
    const response = await apiClient.get(`/pm/tasks/${id}`)
    const data = response.data as { task?: PMTask }
    return data?.task ?? (response.data as PMTask)
  },

  create: async (data: CreatePMTaskRequest): Promise<PMTask> => {
    const response = await apiClient.post('/pm/tasks', data)
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  update: async (id: string, data: Partial<CreatePMTaskRequest>): Promise<PMTask> => {
    const response = await apiClient.put(`/pm/tasks/${id}`, data)
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  updateState: async (id: string, stateId: string): Promise<PMTask> => {
    const response = await apiClient.put(`/pm/tasks/${id}/state`, { state_id: stateId })
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${id}`)
  },

  // Reorder task (update sort_order and optionally state)
  reorder: async (id: string, data: { sort_order: number; state_id?: string }): Promise<PMTask> => {
    const response = await apiClient.put(`/pm/tasks/${id}/reorder`, data)
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  // Bulk reorder tasks (update sort_order for multiple tasks)
  bulkReorder: async (
    updates: { task_id: string; sort_order: number }[]
  ): Promise<BulkOperationResponse> => {
    const response = await apiClient.put('/pm/tasks/bulk/reorder', { updates })
    return response.data as BulkOperationResponse
  },

  // Bulk update state
  bulkUpdateState: async (taskIds: string[], newStateId: string): Promise<BulkOperationResponse> => {
    const response = await apiClient.put('/pm/tasks/bulk/state', {
      task_ids: taskIds,
      new_state_id: newStateId,
    })
    return response.data as BulkOperationResponse
  },

  // Bulk assign/unassign
  bulkAssign: async (taskIds: string[], assigneeId: string | null): Promise<BulkOperationResponse> => {
    const response = await apiClient.put('/pm/tasks/bulk/assign', {
      task_ids: taskIds,
      assignee_id: assigneeId,
    })
    return response.data as BulkOperationResponse
  },

  // Bulk move to epic/sprint
  bulkMove: async (
    taskIds: string[],
    data: { epic_id?: string | null; sprint_id?: string | null }
  ): Promise<BulkOperationResponse> => {
    const response = await apiClient.put('/pm/tasks/bulk/move', {
      task_ids: taskIds,
      epic_id: data.epic_id ?? null,
      sprint_id: data.sprint_id ?? null,
    })
    return response.data as BulkOperationResponse
  },

  // Bulk delete
  bulkDelete: async (taskIds: string[]): Promise<BulkOperationResponse> => {
    const response = await apiClient.delete('/pm/tasks/bulk', {
      data: { task_ids: taskIds },
    })
    return response.data as BulkOperationResponse
  },

  // Assign/unassign task
  assign: async (id: string, assigneeId: string | null): Promise<PMTask> => {
    const response = await apiClient.put(`/pm/tasks/${id}/assign`, { assignee_id: assigneeId })
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  // Move to sprint/cycle
  moveToCycle: async (id: string, cycleId: string | null): Promise<PMTask> => {
    const response = await apiClient.put(`/pm/tasks/${id}/sprint`, { sprint_id: cycleId })
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },

  // Log time spent
  logTime: async (id: string, hours: number, description?: string): Promise<PMTask> => {
    const response = await apiClient.post(`/pm/tasks/${id}/log-time`, { hours, description })
    const payload = response.data as { task?: PMTask }
    return payload?.task ?? (response.data as PMTask)
  },
}

// PM Task Views API (Kanban/Calendar helpers)
export const pmViewsApi = {
  moveTask: async (payload: {
    task_id: string
    new_state_id: string
    new_position: number
  }): Promise<void> => {
    await apiClient.put('/pm/views/kanban/move', payload)
  },
  bulkReorderPositions: async (
    updates: { task_id: string; position: number }[]
  ): Promise<void> => {
    await apiClient.put('/pm/views/kanban/reorder', { updates })
  },
}

// Task Time Entries API
export const taskTimeEntriesApi = {
  list: async (taskId: string): Promise<TaskTimeEntry[]> => {
    const response = await apiClient.get(`/pm/tasks/${taskId}/time-entries`)
    const data = response.data as { time_entries?: TaskTimeEntry[] }
    return data?.time_entries ?? []
  },

  get: async (taskId: string, entryId: string): Promise<TaskTimeEntry> => {
    const response = await apiClient.get(`/pm/tasks/${taskId}/time-entries/${entryId}`)
    const data = response.data as { time_entry?: TaskTimeEntry }
    return data?.time_entry ?? (response.data as TaskTimeEntry)
  },

  create: async (taskId: string, data: CreateTaskTimeEntryRequest): Promise<TaskTimeEntry> => {
    const response = await apiClient.post(`/pm/tasks/${taskId}/time-entries`, data)
    const payload = response.data as { time_entry?: TaskTimeEntry }
    return payload?.time_entry ?? (response.data as TaskTimeEntry)
  },

  update: async (
    taskId: string,
    entryId: string,
    data: CreateTaskTimeEntryRequest
  ): Promise<TaskTimeEntry> => {
    const response = await apiClient.put(`/pm/tasks/${taskId}/time-entries/${entryId}`, data)
    const payload = response.data as { time_entry?: TaskTimeEntry }
    return payload?.time_entry ?? (response.data as TaskTimeEntry)
  },

  delete: async (taskId: string, entryId: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/time-entries/${entryId}`)
  },
}

// Task Blocks API (dependencies)
export const taskBlocksApi = {
  // List tasks that this task blocks
  listBlocks: async (taskId: string): Promise<PMTask[]> => {
    const response = await apiClient.get(`/pm/tasks/${taskId}/blocks`)
    const data = response.data as { tasks?: PMTask[]; blocks?: PMTask[] }
    return data?.tasks ?? data?.blocks ?? []
  },

  // List tasks that block this task
  listBlockedBy: async (taskId: string): Promise<PMTask[]> => {
    const response = await apiClient.get(`/pm/tasks/${taskId}/blocked-by`)
    const data = response.data as { tasks?: PMTask[]; blocked_by?: PMTask[] }
    return data?.tasks ?? data?.blocked_by ?? []
  },

  // Create a block relationship (taskId blocks targetTaskId)
  createBlock: async (taskId: string, targetTaskId: string): Promise<void> => {
    await apiClient.post(`/pm/tasks/${taskId}/blocks`, { blocked_task_id: targetTaskId })
  },

  // Remove a block relationship
  removeBlock: async (blockId: string): Promise<void> => {
    await apiClient.delete(`/pm/task-blocks/${blockId}`)
  },
}

// Workflow States API
export const workflowStatesApi = {
  listByProject: async (projectId: string): Promise<WorkflowState[]> => {
    const response = await apiClient.get(`/pm/projects/${projectId}/states`)
    const data = response.data as { states?: WorkflowState[] }
    return data?.states ?? []
  },
}

// Estimate Templates API
export const estimatesApi = {
  listTemplates: async (projectId: string): Promise<EstimateTemplate[]> => {
    const response = await apiClient.get(`/pm/projects/${projectId}/estimates`)
    const data = response.data as { items?: EstimateTemplate[]; templates?: EstimateTemplate[] }
    return data?.items ?? data?.templates ?? []
  },

  listPoints: async (templateId: string): Promise<EstimatePoint[]> => {
    const response = await apiClient.get(`/pm/estimates/${templateId}/points`)
    const data = response.data as { items?: EstimatePoint[]; points?: EstimatePoint[] }
    return data?.items ?? data?.points ?? []
  },
}

// Cycles/Sprints API
export const cyclesApi = {
  list: async (projectId: string): Promise<Cycle[]> => {
    const response = await apiClient.get(`/pm/projects/${projectId}/cycles`)
    const data = response.data as { cycles?: Cycle[]; items?: Cycle[] }
    return data?.cycles ?? data?.items ?? []
  },

  get: async (cycleId: string): Promise<Cycle> => {
    const response = await apiClient.get(`/pm/cycles/${cycleId}`)
    const data = response.data as { cycle?: Cycle }
    return data?.cycle ?? (response.data as Cycle)
  },

  create: async (projectId: string, data: CreateCycleRequest): Promise<Cycle> => {
    const response = await apiClient.post(`/pm/projects/${projectId}/cycles`, data)
    const payload = response.data as { cycle?: Cycle }
    return payload?.cycle ?? (response.data as Cycle)
  },

  update: async (cycleId: string, data: UpdateCycleRequest): Promise<Cycle> => {
    const response = await apiClient.put(`/pm/cycles/${cycleId}`, data)
    const payload = response.data as { cycle?: Cycle }
    return payload?.cycle ?? (response.data as Cycle)
  },

  delete: async (cycleId: string): Promise<void> => {
    await apiClient.delete(`/pm/cycles/${cycleId}`)
  },

  // Add task to cycle
  addTask: async (cycleId: string, taskId: string): Promise<void> => {
    await apiClient.post(`/pm/cycles/${cycleId}/tasks`, { task_id: taskId })
  },

  // Remove task from cycle
  removeTask: async (cycleId: string, taskId: string): Promise<void> => {
    await apiClient.delete(`/pm/cycles/${cycleId}/tasks/${taskId}`)
  },

  // List tasks in cycle
  listTasks: async (cycleId: string): Promise<PMTask[]> => {
    const response = await apiClient.get(`/pm/cycles/${cycleId}/tasks`)
    const data = response.data as { tasks?: PMTask[]; items?: PMTask[] }
    return data?.tasks ?? data?.items ?? []
  },

  // Get active cycle for project
  getActive: async (projectId: string): Promise<Cycle | null> => {
    const cycles = await cyclesApi.list(projectId)
    return cycles.find(c => c.status === 'active') ?? null
  },
}

// Task Comments API
export const taskCommentsApi = {
  list: async (taskId: string): Promise<TaskComment[]> => {
    const response = await apiClient.get<{ success: boolean; data: TaskComment[] }>(
      `/pm/tasks/${taskId}/comments`
    )
    return response.data.data ?? []
  },

  create: async (taskId: string, data: CreateTaskCommentRequest): Promise<TaskComment> => {
    const response = await apiClient.post<{ success: boolean; data: TaskComment }>(
      `/pm/tasks/${taskId}/comments`,
      data
    )
    return response.data.data
  },

  update: async (taskId: string, commentId: string, data: { content: string }): Promise<TaskComment> => {
    const response = await apiClient.put<{ success: boolean; data: TaskComment }>(
      `/pm/tasks/${taskId}/comments/${commentId}`,
      data
    )
    return response.data.data
  },

  delete: async (taskId: string, commentId: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/comments/${commentId}`)
  },

  count: async (taskId: string): Promise<number> => {
    const response = await apiClient.get<{ success: boolean; data: { count: number } }>(
      `/pm/tasks/${taskId}/comments/count`
    )
    return response.data.data?.count ?? 0
  },
}

// Task Links API
export const taskLinksApi = {
  list: async (taskId: string): Promise<TaskLink[]> => {
    const response = await apiClient.get<{ success: boolean; data: TaskLink[] }>(
      `/pm/tasks/${taskId}/links`
    )
    return response.data.data ?? []
  },

  create: async (taskId: string, data: CreateTaskLinkRequest): Promise<TaskLink> => {
    const response = await apiClient.post<{ success: boolean; data: TaskLink }>(
      `/pm/tasks/${taskId}/links`,
      data
    )
    return response.data.data
  },

  update: async (taskId: string, linkId: string, data: { title?: string; description?: string }): Promise<TaskLink> => {
    const response = await apiClient.put<{ success: boolean; data: TaskLink }>(
      `/pm/tasks/${taskId}/links/${linkId}`,
      data
    )
    return response.data.data
  },

  delete: async (taskId: string, linkId: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/links/${linkId}`)
  },

  count: async (taskId: string): Promise<number> => {
    const response = await apiClient.get<{ success: boolean; data: { count: number } }>(
      `/pm/tasks/${taskId}/links/count`
    )
    return response.data.data?.count ?? 0
  },
}

// Task Attachments API
export const taskAttachmentsApi = {
  list: async (taskId: string): Promise<TaskAttachment[]> => {
    const response = await apiClient.get<{ success: boolean; attachments: TaskAttachment[] }>(
      `/pm/tasks/${taskId}/attachments`
    )
    return response.data.attachments ?? []
  },

  create: async (taskId: string, data: CreateTaskAttachmentRequest): Promise<TaskAttachment> => {
    const response = await apiClient.post<{ success: boolean; attachment: TaskAttachment }>(
      `/pm/tasks/${taskId}/attachments`,
      data
    )
    return response.data.attachment
  },

  delete: async (taskId: string, attachmentId: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/attachments/${attachmentId}`)
  },

  count: async (taskId: string): Promise<{ count: number; total_size: number }> => {
    const response = await apiClient.get<{ success: boolean; count: number; total_size: number }>(
      `/pm/tasks/${taskId}/attachments/count`
    )
    return {
      count: response.data.count ?? 0,
      total_size: response.data.total_size ?? 0,
    }
  },
}

// Task Watchers API
export const taskWatchersApi = {
  list: async (taskId: string): Promise<TaskWatcher[]> => {
    const response = await apiClient.get<{ success: boolean; watchers: TaskWatcher[] }>(
      `/pm/tasks/${taskId}/watchers`
    )
    return response.data.watchers ?? []
  },

  status: async (taskId: string): Promise<TaskWatchStatus> => {
    const response = await apiClient.get<{ success: boolean; status: TaskWatchStatus }>(
      `/pm/tasks/${taskId}/watchers/status`
    )
    return response.data.status
  },

  watch: async (taskId: string, data?: TaskWatchSettingsRequest): Promise<TaskWatcher> => {
    const response = await apiClient.post<{ success: boolean; watcher: TaskWatcher }>(
      `/pm/tasks/${taskId}/watchers`,
      data ?? {}
    )
    return response.data.watcher
  },

  unwatch: async (taskId: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/watchers`)
  },

  updateSettings: async (taskId: string, data: TaskWatchSettingsRequest): Promise<TaskWatcher> => {
    const response = await apiClient.put<{ success: boolean; watcher: TaskWatcher }>(
      `/pm/tasks/${taskId}/watchers/settings`,
      data
    )
    return response.data.watcher
  },
}

// Task Activity API
export const taskActivityApi = {
  list: async (taskId: string, params?: { limit?: number }): Promise<TaskActivityEvent[]> => {
    const response = await apiClient.get<{ success: boolean; data: TaskActivityEvent[] }>(
      `/pm/tasks/${taskId}/activity`,
      { params }
    )
    return response.data.data ?? []
  },
}

// Task Subtasks API
export const taskSubtasksApi = {
  list: async (taskId: string): Promise<PMTask[]> => {
    const response = await apiClient.get<{ success: boolean; subtasks: PMTask[] }>(
      `/pm/tasks/${taskId}/subtasks`
    )
    return response.data.subtasks ?? []
  },

  create: async (parentTaskId: string, data: Omit<CreatePMTaskRequest, 'parent_task_id'>): Promise<PMTask> => {
    const response = await apiClient.post<{ success: boolean; data: PMTask }>(
      '/pm/tasks',
      { ...data, parent_task_id: parentTaskId }
    )
    return response.data.data
  },
}

// Task Relationships API
export const taskRelationshipsApi = {
  list: async (taskId: string): Promise<TaskRelationship[]> => {
    const response = await apiClient.get<{ success: boolean; relationships: TaskRelationship[] }>(
      `/pm/tasks/${taskId}/relationships`
    )
    return response.data.relationships ?? []
  },

  create: async (taskId: string, data: { target_task_id: string; relationship_type: string; description?: string }): Promise<TaskRelationship> => {
    const response = await apiClient.post<{ success: boolean; relationship: TaskRelationship }>(
      `/pm/tasks/${taskId}/relationships`,
      data
    )
    return response.data.relationship
  },

  delete: async (taskId: string, targetTaskId: string, relationshipType: string): Promise<void> => {
    await apiClient.delete(`/pm/tasks/${taskId}/relationships/${targetTaskId}?type=${relationshipType}`)
  },
}

// Support Tickets API
export const ticketsApi = {
  list: async (params?: {
    status?: string
    priority?: string
    category_id?: string
    assignee_id?: string
    limit?: number
    offset?: number
  }): Promise<SupportTicket[]> => {
    const response = await apiClient.get<ApiResponse<SupportTicket[]>>('/tickets', { params })
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list tickets')
    return response.data.data
  },

  search: async (q: string, limit: number = 50): Promise<SupportTicket[]> => {
    const response = await apiClient.get<ApiResponse<SupportTicket[]>>('/tickets/search', {
      params: { q, limit },
    })
    if (!response.data.success) throw new Error(response.data.error || 'Failed to search tickets')
    return response.data.data
  },

  get: async (id: string): Promise<SupportTicket> => {
    const response = await apiClient.get<ApiResponse<SupportTicket>>(`/tickets/${id}`)
    if (!response.data.success) throw new Error(response.data.error || 'Failed to load ticket')
    return response.data.data
  },

  create: async (data: CreateTicketRequest): Promise<SupportTicket> => {
    const response = await apiClient.post<ApiResponse<SupportTicket>>('/tickets', data)
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create ticket')
    return response.data.data
  },

  update: async (id: string, data: UpdateTicketRequest): Promise<SupportTicket> => {
    const response = await apiClient.put<ApiResponse<SupportTicket>>(`/tickets/${id}`, data)
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update ticket')
    return response.data.data
  },

  updateStatus: async (id: string, status: string): Promise<SupportTicket> => {
    const response = await apiClient.put<ApiResponse<SupportTicket>>(`/tickets/${id}/status`, { status })
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update status')
    return response.data.data
  },

  assign: async (id: string, assigneeId: string): Promise<SupportTicket> => {
    const response = await apiClient.put<ApiResponse<SupportTicket>>(`/tickets/${id}/assign`, { assignee_id: assigneeId })
    if (!response.data.success) throw new Error(response.data.error || 'Failed to assign ticket')
    return response.data.data
  },

  getStats: async (): Promise<TicketStats> => {
    const response = await apiClient.get<ApiResponse<TicketStats>>('/tickets/stats')
    if (!response.data.success) throw new Error(response.data.error || 'Failed to load stats')
    return response.data.data
  },

  listCategories: async (): Promise<TicketCategory[]> => {
    const response = await apiClient.get<ApiResponse<TicketCategory[]>>('/tickets/categories')
    if (!response.data.success) throw new Error(response.data.error || 'Failed to load categories')
    return response.data.data
  },

  listMessages: async (ticketId: string, includeInternal: boolean = true): Promise<SupportTicketMessage[]> => {
    const response = await apiClient.get<ApiResponse<SupportTicketMessage[]>>(`/tickets/${ticketId}/messages`, {
      params: includeInternal ? { include_internal: 'true' } : undefined,
    })
    if (!response.data.success) throw new Error(response.data.error || 'Failed to load messages')
    return response.data.data
  },

  addMessage: async (ticketId: string, data: AddTicketMessageRequest): Promise<SupportTicketMessage> => {
    const response = await apiClient.post<ApiResponse<SupportTicketMessage>>(`/tickets/${ticketId}/messages`, data)
    if (!response.data.success) throw new Error(response.data.error || 'Failed to send message')
    return response.data.data
  },

  emailReply: async (
    ticketId: string,
    data: { to: string; subject: string; body: string; in_reply_to?: string }
  ): Promise<SupportTicketMessage> => {
    const response = await apiClient.post<ApiResponse<SupportTicketMessage>>(`/tickets/${ticketId}/email/reply`, data)
    if (!response.data.success) throw new Error(response.data.error || 'Failed to send email reply')
    return response.data.data
  },
}

// SaaS Admin Support API
export const supportAdminApi = {
  listTickets: async (params: {
    search?: string
    status?: string
    priority?: string
    organization?: string
    category?: string
    assigned_to?: string
    page?: number
    per_page?: number
  }): Promise<{
    tickets: SupportAdminTicketSummary[]
    pagination: { total: number; page: number; per_page: number; total_pages: number }
  }> => {
    const response = await saasAdminApiClient.get<{
      tickets: SupportAdminTicketSummary[]
      pagination: { total: number; page: number; per_page: number; total_pages: number }
    }>('/saas-admin/api/support/tickets/', { params })
    return response.data
  },

  getTicket: async (ticketId: string): Promise<SupportAdminTicketDetail> => {
    const response = await saasAdminApiClient.get<SupportAdminTicketDetail>(
      `/saas-admin/api/support/tickets/${ticketId}/`
    )
    return response.data
  },

  updateTicket: async (
    ticketId: string,
    data: {
      status?: string
      priority?: string
      assigned_to?: string | null
      category?: string | null
    }
  ): Promise<{ success: boolean }> => {
    const response = await saasAdminApiClient.put<{ success: boolean }>(
      `/saas-admin/api/support/tickets/${ticketId}/`,
      data
    )
    return response.data
  },

  replyToTicket: async (
    ticketId: string,
    data: { content: string; is_internal: boolean }
  ): Promise<{ success: boolean; message_id: string }> => {
    const response = await saasAdminApiClient.post<{ success: boolean; message_id: string }>(
      `/saas-admin/api/support/tickets/${ticketId}/reply/`,
      data
    )
    return response.data
  },

  getStats: async (): Promise<SupportAdminStats> => {
    const response = await saasAdminApiClient.get<SupportAdminStats>(
      '/saas-admin/api/support/stats/'
    )
    return response.data
  },

  getFilters: async (): Promise<SupportAdminFilters> => {
    const response = await saasAdminApiClient.get<SupportAdminFilters>(
      '/saas-admin/api/support/filters/'
    )
    return response.data
  },
}

// Messaging API
export const messagingApi = {
  createAccount: async (data: {
    provider: 'whatsapp' | 'telegram'
    phone_number?: string
    username?: string
    display_name: string
    connection_type?: string
    business_id?: string
    cloud?: {
      phone_number_id?: string
      business_id?: string
      business_account_id?: string
      access_token?: string
      api_version?: string
      verify_token?: string
      display_phone?: string
      display_phone_number?: string
      verified_name?: string
    }
  }): Promise<MessagingAccount> => {
    const response = await apiClient.post<MessagingAccount>('/messaging/accounts', data)
    return response.data
  },

  listAccounts: async (): Promise<MessagingAccount[]> => {
    const response = await apiClient.get<{ success: boolean; accounts: MessagingAccount[] }>(
      '/messaging/accounts'
    )
    return response.data.accounts ?? []
  },

  listLocations: async (): Promise<MessagingLocation[]> => {
    const response = await apiClient.get<{ success: boolean; locations: MessagingLocation[] }>(
      '/messaging/locations'
    )
    return response.data.locations ?? []
  },

  connectAccount: async (accountId: string, provider: 'whatsapp' | 'telegram'): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post<{ success: boolean; message?: string }>(
      `/messaging/accounts/${accountId}/connect`,
      { provider }
    )
    return response.data
  },

  disconnectAccount: async (accountId: string, provider: 'whatsapp' | 'telegram'): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/messaging/accounts/${accountId}/disconnect`,
      { provider }
    )
    return response.data
  },

  getQRCode: async (accountId: string): Promise<{ success: boolean; qr_code?: string; expires_at?: string }> => {
    const response = await apiClient.get<{ success: boolean; qr_code?: string; expires_at?: string }>(
      `/messaging/accounts/${accountId}/qr`
    )
    return response.data
  },

  getAccountStatus: async (accountId: string): Promise<MessagingAccountStatus> => {
    const response = await apiClient.get<MessagingAccountStatus>(
      `/messaging/accounts/${accountId}/status`
    )
    return response.data
  },

  listConversations: async (accountId: string): Promise<MessagingConversation[]> => {
    const response = await apiClient.get<{ success: boolean; conversations: MessagingConversation[] }>(
      `/messaging/accounts/${accountId}/conversations`
    )
    return response.data.conversations ?? []
  },

  getMessages: async (conversationId: string): Promise<MessagingMessage[]> => {
    const response = await apiClient.get<{ success: boolean; messages: MessagingMessage[] }>(
      `/messaging/conversations/${conversationId}/messages`
    )
    return response.data.messages ?? []
  },

  markConversationRead: async (conversationId: string): Promise<void> => {
    await apiClient.post(`/messaging/conversations/${conversationId}/read`)
  },

  markConversationUnread: async (conversationId: string): Promise<void> => {
    await apiClient.post(`/messaging/conversations/${conversationId}/unread`)
  },

  startTelegramAuth: async (accountId: string, phoneNumber: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post<{ success: boolean; message?: string }>(
      `/messaging/accounts/${accountId}/telegram/start`,
      { phone_number: phoneNumber }
    )
    return response.data
  },

  submitTelegramCode: async (accountId: string, code: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/messaging/accounts/${accountId}/telegram/code`,
      { code }
    )
    return response.data
  },

  submitTelegramPassword: async (accountId: string, password: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/messaging/accounts/${accountId}/telegram/password`,
      { password }
    )
    return response.data
  },

  sendMessage: async (data: SendMessageRequest): Promise<SendMessageResponse> => {
    const response = await apiClient.post<SendMessageResponse>('/messaging/messages', data)
    return response.data
  },
}

// User Profile types
export interface UserProfile {
  id: string
  email: string
  first_name?: string
  last_name?: string
  role?: string
  avatar_url?: string
  timezone?: string
  language?: string
}

export interface UpdateProfileRequest {
  email?: string
  first_name?: string
  last_name?: string
  role?: string
  timezone?: string
  language?: string
}

export interface NotificationSettings {
  email_notifications: boolean
  push_notifications: boolean
  deal_updates: boolean
  task_reminders: boolean
  weekly_digest: boolean
}

export type AIProvider = 'kimi' | 'openai' | 'gemini' | 'anthropic'

export interface AIModelInfo {
  id: string
  name: string
  provider: AIProvider
  description: string
  max_tokens: number
  available: boolean
}

export interface ProviderModel {
  id: string
  name: string
  description?: string
  context_length?: number
  is_default?: boolean
}

export interface ProviderModelsResponse {
  provider: AIProvider
  models: ProviderModel[]
  error?: string
}

export interface AITaskInfo {
  id: string
  default_model: string
}

export interface AIListModelsResponse {
  models: AIModelInfo[]
  default_model: string
  tasks: AITaskInfo[]
  task_models: Record<string, string>
  default_task_models: Record<string, string>
}

export interface AIKeyStatus {
  configured: boolean
  base_url?: string
  masked_key?: string
  selected_model?: string
}

export interface AIKeysResponse {
  kimi?: AIKeyStatus
  openai?: AIKeyStatus
  gemini?: AIKeyStatus
  anthropic?: AIKeyStatus
}

export interface Session {
  id: string
  device: string
  location: string
  current: boolean
  created_at: string
}

// Settings API
export const settingsApi = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>('/users/me')
    return response.data
  },

  updateProfile: async (data: UpdateProfileRequest): Promise<UserProfile> => {
    const response = await apiClient.put<UserProfile>('/users/me', data)
    return response.data
  },

  getNotificationSettings: async (): Promise<NotificationSettings> => {
    const response = await apiClient.get<NotificationSettings>('/users/me/notifications')
    return response.data
  },

  updateNotificationSettings: async (data: Partial<NotificationSettings>): Promise<NotificationSettings> => {
    const response = await apiClient.put<NotificationSettings>('/users/me/notifications', data)
    return response.data
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<void> => {
    await apiClient.post('/users/me/password', data)
  },

  getSessions: async (): Promise<Session[]> => {
    const response = await apiClient.get<Session[]>('/users/me/sessions')
    return response.data
  },

  revokeSession: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/me/sessions/${id}`)
  },
}

export const aiSettingsApi = {
  getKeys: async (): Promise<AIKeysResponse> => {
    const response = await apiClient.get<AIKeysResponse>('/settings/ai/keys')
    return response.data
  },

  setKeys: async (data: {
    kimi_api_key?: string
    kimi_base_url?: string
    kimi_model?: string
    openai_api_key?: string
    openai_base_url?: string
    openai_model?: string
    gemini_api_key?: string
    gemini_base_url?: string
    gemini_model?: string
    anthropic_api_key?: string
    anthropic_base_url?: string
    anthropic_model?: string
  }): Promise<{ message?: string }> => {
    const response = await apiClient.put<{ message?: string }>('/settings/ai/keys', data)
    return response.data
  },

  deleteKey: async (provider: AIProvider): Promise<{ message?: string }> => {
    const response = await apiClient.delete<{ message?: string }>(`/settings/ai/keys/${provider}`)
    return response.data
  },

  getProviderModels: async (provider: AIProvider, apiKey?: string, baseUrl?: string): Promise<ProviderModelsResponse> => {
    const response = await apiClient.post<ProviderModelsResponse>(`/settings/ai/providers/${provider}/models`, {
      api_key: apiKey,
      base_url: baseUrl,
    })
    return response.data
  },

  setProviderModel: async (provider: AIProvider, model: string): Promise<{ message?: string }> => {
    const response = await apiClient.put<{ message?: string }>(`/settings/ai/keys/${provider}/model`, { model })
    return response.data
  },

  listModels: async (): Promise<AIListModelsResponse> => {
    const response = await apiClient.get<AIListModelsResponse>('/settings/ai/models')
    return response.data
  },

  setDefaultModel: async (model: string): Promise<{ message?: string; model?: string }> => {
    const response = await apiClient.put<{ message?: string; model?: string }>(
      '/settings/ai/models/default',
      { model }
    )
    return response.data
  },

  setTaskModels: async (taskModels: Record<string, string>): Promise<{ message?: string }> => {
    const response = await apiClient.put<{ message?: string }>(
      '/settings/ai/models/tasks',
      { task_models: taskModels }
    )
    return response.data
  },

  testModel: async (data: { model: string; prompt?: string }): Promise<{
    success: boolean
    model: string
    provider?: string
    response?: string
    error?: string
  }> => {
    const response = await apiClient.post('/settings/ai/models/test', data)
    return response.data
  },
}

// Onboarding API
export interface WorkspaceSetupRequest {
  name: string
  domain: string
  allow_teammates: boolean
}

export interface WorkspaceSetupResponse {
  id: string
  name: string
  slug: string
  domain: string
  created_at: string
}

export const onboardingApi = {
  setupWorkspace: async (data: WorkspaceSetupRequest): Promise<WorkspaceSetupResponse> => {
    const response = await apiClient.post<WorkspaceSetupResponse>('/onboarding/workspace', data)
    return response.data
  },

  getOnboardingStatus: async (): Promise<{
    completed: boolean
    steps: {
      workspace: boolean
      email_connection: boolean
    }
  }> => {
    const response = await apiClient.get('/onboarding/status')
    return response.data
  },
}

// Channels API - Multi-channel per project management
import type {
  Channel,
  ChannelType,
  ChannelScope,
  CreateChannelRequest,
  UpdateChannelRequest,
  UpdateChannelScopeRequest,
  AssignChannelProjectsRequest,
} from '@/types/api'

export const channelsApi = {
  // List all channels for the organization
  list: async (params?: {
    type?: ChannelType
    scope?: ChannelScope
    page?: number
    page_size?: number
  }): Promise<{ items: Channel[]; page: number; page_size: number }> => {
    const response = await apiClient.get('/channels', { params })
    return response.data
  },

  // Get a channel by ID
  get: async (id: string): Promise<Channel> => {
    const response = await apiClient.get<Channel>(`/channels/${id}`)
    return response.data
  },

  // Create a new channel
  create: async (data: CreateChannelRequest): Promise<Channel> => {
    const response = await apiClient.post<Channel>('/channels', data)
    return response.data
  },

  // Update a channel
  update: async (id: string, data: UpdateChannelRequest): Promise<Channel> => {
    const response = await apiClient.put<Channel>(`/channels/${id}`, data)
    return response.data
  },

  // Delete a channel (soft delete)
  delete: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/channels/${id}`)
    return response.data
  },

  // Update channel scope (global vs projects)
  updateScope: async (id: string, data: UpdateChannelScopeRequest): Promise<Channel> => {
    const response = await apiClient.put<Channel>(`/channels/${id}/scope`, data)
    return response.data
  },

  // Update channel connected status
  updateConnected: async (id: string, isConnected: boolean): Promise<{ success: boolean; is_connected: boolean }> => {
    const response = await apiClient.put<{ success: boolean; is_connected: boolean }>(
      `/channels/${id}/connected`,
      { is_connected: isConnected }
    )
    return response.data
  },

  // List projects assigned to a channel
  listProjects: async (channelId: string): Promise<{ items: Project[] }> => {
    const response = await apiClient.get<{ items: Project[] }>(`/channels/${channelId}/projects`)
    return response.data
  },

  // Assign channel to projects
  assignToProjects: async (channelId: string, data: AssignChannelProjectsRequest): Promise<{ success: boolean; count: number }> => {
    const response = await apiClient.post<{ success: boolean; count: number }>(
      `/channels/${channelId}/projects`,
      data
    )
    return response.data
  },

  // Remove channel from a project
  removeFromProject: async (channelId: string, projectId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(
      `/channels/${channelId}/projects/${projectId}`
    )
    return response.data
  },

  // Set channel as default for a project
  setProjectDefault: async (channelId: string, projectId: string, isDefault: boolean): Promise<{ success: boolean; is_default: boolean }> => {
    const response = await apiClient.put<{ success: boolean; is_default: boolean }>(
      `/channels/${channelId}/projects/${projectId}/default`,
      { is_default: isDefault }
    )
    return response.data
  },

  // Get channels for a project (includes global + assigned)
  getForProject: async (projectId: string): Promise<{ items: Channel[] }> => {
    const response = await apiClient.get<{ items: Channel[] }>(`/projects/${projectId}/channels`)
    return response.data
  },

  // Get default channel for a project by type
  getDefaultForProject: async (projectId: string, channelType: ChannelType): Promise<Channel> => {
    const response = await apiClient.get<Channel>(`/projects/${projectId}/channels/default/${channelType}`)
    return response.data
  },
}

// Team Invitations API
import type {
  Invitation,
  PublicInvitation,
  TeamMember,
  ProjectMember,
  AddProjectMemberRequest,
  CreateInvitationRequest,
  UpdateRoleRequest,
  InvitationStatus,
} from '@/types/api'

export const invitationsApi = {
  // Create a new invitation
  create: async (data: CreateInvitationRequest): Promise<{ success: boolean; invitation: Invitation; invite_link: string }> => {
    const response = await apiClient.post<{ success: boolean; invitation: Invitation; invite_link: string }>(
      '/invitations',
      data
    )
    return response.data
  },

  // List invitations for the organization
  list: async (status?: InvitationStatus): Promise<{ items: Invitation[] }> => {
    const params = status ? { status } : undefined
    const response = await apiClient.get<{ items: Invitation[] }>('/invitations', { params })
    return response.data
  },

  // Get an invitation by ID
  get: async (id: string): Promise<Invitation> => {
    const response = await apiClient.get<Invitation>(`/invitations/${id}`)
    return response.data
  },

  // Cancel an invitation
  cancel: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/invitations/${id}`)
    return response.data
  },

  // Resend invitation email
  resend: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/invitations/${id}/resend`)
    return response.data
  },

  // Get invitation by token (public)
  getByToken: async (token: string): Promise<PublicInvitation> => {
    const response = await apiClient.get<PublicInvitation>(`/invite/${token}`)
    return response.data
  },

  // Accept invitation (public, but may require auth)
  accept: async (token: string): Promise<{ success: boolean; organization_id: string; role: string }> => {
    const response = await apiClient.post<{ success: boolean; organization_id: string; role: string }>(
      `/invite/${token}/accept`
    )
    return response.data
  },
}

export const teamApi = {
  // List team members
  list: async (): Promise<{ items: TeamMember[] }> => {
    const response = await apiClient.get<{ items: TeamMember[] }>('/team')
    return response.data
  },

  // Update member role
  updateRole: async (userId: string, data: UpdateRoleRequest): Promise<{ success: boolean; role: string }> => {
    const response = await apiClient.put<{ success: boolean; role: string }>(
      `/team/members/${userId}/role`,
      data
    )
    return response.data
  },

  // Remove member
  remove: async (userId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/team/members/${userId}`)
    return response.data
  },
}

export const projectMembersApi = {
  // List project members
  list: async (projectId: string): Promise<{ success: boolean; members: ProjectMember[]; count: number }> => {
    const response = await apiClient.get<{ success: boolean; members: ProjectMember[]; count: number }>(
      `/projects/${projectId}/members`
    )
    return response.data
  },

  // Add project member
  add: async (projectId: string, data: AddProjectMemberRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(`/projects/${projectId}/members`, data)
    return response.data
  },

  // Remove project member
  remove: async (projectId: string, userId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/projects/${projectId}/members/${userId}`)
    return response.data
  },
}

// Pages (Documentation) API
import type {
  Page,
  CreatePageRequest,
  UpdatePageRequest,
  UpdatePageContentRequest,
  MovePageRequest,
} from '@/types/api'

export const pagesApi = {
  // List all pages
  list: async (): Promise<Page[]> => {
    const response = await apiClient.get<{ items?: Page[] }>('/pages')
    return response.data.items ?? []
  },

  // List pages by project
  listByProject: async (projectId: string): Promise<Page[]> => {
    const response = await apiClient.get<{ items?: Page[] }>(`/projects/${projectId}/pages`)
    return response.data.items ?? []
  },

  // Get a page by ID
  get: async (pageId: string): Promise<Page> => {
    const response = await apiClient.get<Page>(`/pages/${pageId}`)
    return response.data
  },

  // Create a page
  create: async (data: CreatePageRequest): Promise<Page> => {
    const response = await apiClient.post<Page>('/pages', data)
    return response.data
  },

  // Update a page
  update: async (pageId: string, data: UpdatePageRequest): Promise<Page> => {
    const response = await apiClient.put<Page>(`/pages/${pageId}`, data)
    return response.data
  },

  // Update page content only
  updateContent: async (pageId: string, data: UpdatePageContentRequest): Promise<Page> => {
    const response = await apiClient.put<Page>(`/pages/${pageId}/content`, data)
    return response.data
  },

  // Archive a page
  archive: async (pageId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/pages/${pageId}`)
    return response.data
  },

  // Lock a page
  lock: async (pageId: string): Promise<Page> => {
    const response = await apiClient.post<Page>(`/pages/${pageId}/lock`)
    return response.data
  },

  // Unlock a page
  unlock: async (pageId: string): Promise<Page> => {
    const response = await apiClient.post<Page>(`/pages/${pageId}/unlock`)
    return response.data
  },

  // List children of a page
  listChildren: async (pageId: string): Promise<Page[]> => {
    const response = await apiClient.get<{ items?: Page[] }>(`/pages/${pageId}/children`)
    return response.data.items ?? []
  },

  // Move a page
  move: async (pageId: string, data: MovePageRequest): Promise<Page> => {
    const response = await apiClient.put<Page>(`/pages/${pageId}/move`, data)
    return response.data
  },

  // Favorite a page
  favorite: async (pageId: string): Promise<Page> => {
    const response = await apiClient.post<Page>(`/pages/${pageId}/favorite`)
    return response.data
  },

  // Unfavorite a page
  unfavorite: async (pageId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/pages/${pageId}/favorite`)
    return response.data
  },

  // Search pages
  search: async (query: string): Promise<Page[]> => {
    const response = await apiClient.get<{ items?: Page[] }>('/pages/search', { params: { q: query } })
    return response.data.items ?? []
  },
}

// ============================================================================
// Context Pack API (AI Semantic Search)
// ============================================================================

import type {
  ContextPack,
  ContextPackRequest,
  ContextPackResponse,
} from '@/types/api'

export const contextPackApi = {
  /**
   * Build a context pack with semantic search results.
   * Uses vector embeddings for similarity matching.
   */
  build: async (params: ContextPackRequest): Promise<ContextPackResponse> => {
    const response = await apiClient.post<ContextPack>('/ai/contextpack/build', params)
    return { context_pack: response.data, total_tokens: response.data.tokens_used }
  },
}

// ============================================================================
// Analytics API
// ============================================================================

export interface AnalyticsDashboard {
  id: string
  name: string
  description?: string
  owner_id: string
  is_default: boolean
  is_shared: boolean
  settings?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface WidgetPosition {
  x: number
  y: number
  width: number
  height: number
}

export type WidgetType = 'number' | 'chart' | 'table' | 'list' | 'funnel' | 'gauge' | 'heatmap'
export type WidgetDataSource = 'deals' | 'tasks' | 'contacts' | 'activities' | 'pipeline' | 'custom'

export interface AnalyticsWidget {
  id: string
  dashboard_id: string
  type: WidgetType
  title: string
  data_source: WidgetDataSource
  config: Record<string, unknown>
  position: WidgetPosition
  created_at: string
  updated_at: string
}

export interface OverviewMetrics {
  total_deals: number
  open_deals: number
  won_deals: number
  lost_deals: number
  total_value: number
  won_value: number
  pipeline_value: number
  avg_deal_value: number
  win_rate: number
  avg_cycle_days: number
}

export interface CoverageMetrics {
  quota_target: number
  current_pipeline: number
  weighted_pipeline: number
  coverage_ratio: number
  status: 'healthy' | 'at_risk' | 'critical'
  gap_to_close: number
}

export interface VelocityMetrics {
  avg_days_to_close: number
  median_days_to_close: number
  deals_per_month: number
  value_per_month: number
  stage_velocity: Array<{
    stage_id: string
    stage_name: string
    avg_days: number
  }>
}

export interface TrendDataPoint {
  date: string
  value: number
  count?: number
}

export interface ForecastData {
  period_start: string
  period_end: string
  expected_value: number
  weighted_value: number
  deal_count: number
  confidence: number
}

export interface LossReason {
  reason: string
  count: number
  value: number
  percentage: number
}

export interface StaleDeal {
  id: string
  title: string
  value: number
  stage_name: string
  days_stale: number
  last_activity?: string
  owner_name?: string
}

export interface AIInsight {
  id: string
  type: 'risk' | 'opportunity' | 'trend' | 'anomaly' | 'recommendation'
  severity: 'low' | 'medium' | 'high' | 'critical'
  category: 'pipeline' | 'deals' | 'performance' | 'forecast' | 'engagement'
  title: string
  description: string
  confidence: number
  is_dismissed: boolean
  action_items?: Array<{
    id: string
    title: string
    action_type: string
    priority: string
    completed: boolean
    action_data?: Record<string, unknown>
  }>
  related_deal_ids?: string[]
  related_pipeline_id?: string
  valid_until?: string
  created_at: string
}

export interface InsightSummary {
  total_active: number
  by_severity: Record<string, number>
  by_category: Record<string, number>
  critical_count: number
}

export interface AnalyticsReport {
  id: string
  name: string
  type: 'pipeline' | 'sales' | 'activity' | 'forecast' | 'custom'
  format: 'pdf' | 'csv' | 'xlsx'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  owner_id: string
  parameters?: Record<string, unknown>
  date_from?: string
  date_to?: string
  file_url?: string
  file_size?: number
  generated_at?: string
  error_message?: string
  created_at: string
}

// ============================================================================
// Team Forecast Types
// ============================================================================

export type ForecastPeriod = 'week' | 'month' | 'quarter'

export interface TeamForecastSummary {
  committed: number
  best_case: number
  pipeline: number
  predicted: number
  confidence: number
}

export interface RepDealSummary {
  open_count: number
  open_value: number
  weighted_value: number
  expected_to_close: number
}

export interface PeriodComparison {
  forecast_change_pct: number
  deals_change_pct: number
}

export interface RepForecast {
  user_id: string
  user_name: string
  avatar_url: string
  quota: number
  forecast: TeamForecastSummary
  quota_attainment: number
  deals: RepDealSummary
  comparison_to_last_period: PeriodComparison
}

export interface TeamForecastOutput {
  period: string
  period_start: string
  period_end: string
  currency?: string
  team_total: TeamForecastSummary
  reps: RepForecast[]
}

export const analyticsApi = {
  // Dashboards
  dashboards: {
    list: async (params?: { limit?: number; offset?: number }): Promise<AnalyticsDashboard[]> => {
      const response = await apiClient.get<ApiResponse<AnalyticsDashboard[]>>('/analytics/dashboards', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to list dashboards')
      return response.data.data
    },

    get: async (id: string): Promise<AnalyticsDashboard> => {
      const response = await apiClient.get<ApiResponse<AnalyticsDashboard>>(`/analytics/dashboards/${id}`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get dashboard')
      return response.data.data
    },

    create: async (data: {
      name: string
      description?: string
      is_default?: boolean
      is_shared?: boolean
      settings?: Record<string, unknown>
    }): Promise<AnalyticsDashboard> => {
      const response = await apiClient.post<ApiResponse<AnalyticsDashboard>>('/analytics/dashboards', data)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to create dashboard')
      return response.data.data
    },

    update: async (id: string, data: {
      name?: string
      description?: string
      is_default?: boolean
      is_shared?: boolean
      settings?: Record<string, unknown>
    }): Promise<AnalyticsDashboard> => {
      const response = await apiClient.put<ApiResponse<AnalyticsDashboard>>(`/analytics/dashboards/${id}`, data)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update dashboard')
      return response.data.data
    },

    delete: async (id: string): Promise<void> => {
      const response = await apiClient.delete<ApiResponse<null>>(`/analytics/dashboards/${id}`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to delete dashboard')
    },
  },

  // Widgets
  widgets: {
    create: async (dashboardId: string, data: {
      type: WidgetType
      title: string
      data_source: WidgetDataSource
      config?: Record<string, unknown>
      position: WidgetPosition
    }): Promise<AnalyticsWidget> => {
      const response = await apiClient.post<ApiResponse<AnalyticsWidget>>(
        `/analytics/dashboards/${dashboardId}/widgets`,
        data
      )
      if (!response.data.success) throw new Error(response.data.error || 'Failed to create widget')
      return response.data.data
    },

    update: async (id: string, data: {
      title?: string
      type?: WidgetType
      data_source?: WidgetDataSource
      config?: Record<string, unknown>
      position?: WidgetPosition
    }): Promise<AnalyticsWidget> => {
      const response = await apiClient.put<ApiResponse<AnalyticsWidget>>(`/analytics/widgets/${id}`, data)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update widget')
      return response.data.data
    },

    delete: async (id: string): Promise<void> => {
      const response = await apiClient.delete<ApiResponse<null>>(`/analytics/widgets/${id}`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to delete widget')
    },

    getData: async (id: string): Promise<Record<string, unknown>> => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(`/analytics/widgets/${id}/data`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get widget data')
      return response.data.data
    },
  },

  // Metrics
  metrics: {
    getOverview: async (pipelineId?: string): Promise<OverviewMetrics> => {
      const params = pipelineId ? { pipeline_id: pipelineId } : undefined
      const response = await apiClient.get<ApiResponse<OverviewMetrics>>('/analytics/metrics/overview', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get overview metrics')
      return response.data.data
    },

    getCoverage: async (pipelineId: string): Promise<CoverageMetrics> => {
      const response = await apiClient.get<ApiResponse<CoverageMetrics>>('/analytics/coverage', {
        params: { pipeline_id: pipelineId },
      })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get coverage metrics')
      return response.data.data
    },

    getPipeline: async (pipelineId: string): Promise<Record<string, unknown>> => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/analytics/metrics/pipeline/${pipelineId}`
      )
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get pipeline metrics')
      return response.data.data
    },

    getVelocity: async (params?: {
      pipeline_id?: string
      from?: string
      to?: string
    }): Promise<VelocityMetrics> => {
      const response = await apiClient.get<ApiResponse<VelocityMetrics>>('/analytics/metrics/velocity', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get velocity metrics')
      return response.data.data
    },

    getTrend: async (key: string, params?: {
      period?: 'day' | 'week' | 'month'
      from?: string
      to?: string
      pipeline_id?: string
    }): Promise<TrendDataPoint[]> => {
      const response = await apiClient.get<ApiResponse<TrendDataPoint[]>>(`/analytics/metrics/trend/${key}`, { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get trend metrics')
      return response.data.data
    },

    getForecast: async (params?: {
      from?: string
      to?: string
      pipeline_id?: string
    }): Promise<ForecastData[]> => {
      const response = await apiClient.get<ApiResponse<ForecastData[]>>('/analytics/metrics/forecast', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get forecast')
      return response.data.data
    },

    getLossReasons: async (params?: {
      pipeline_id?: string
      from?: string
      to?: string
      limit?: number
    }): Promise<LossReason[]> => {
      const response = await apiClient.get<ApiResponse<LossReason[]>>('/analytics/metrics/loss-reasons', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get loss reasons')
      return response.data.data
    },

    getStaleDeals: async (params?: {
      days?: number
      pipeline_id?: string
      limit?: number
    }): Promise<StaleDeal[]> => {
      const response = await apiClient.get<ApiResponse<StaleDeal[]>>('/analytics/metrics/stale-deals', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get stale deals')
      return response.data.data
    },

    getTeamForecast: async (pipelineId: string, period?: ForecastPeriod): Promise<TeamForecastOutput> => {
      const params: Record<string, string> = { pipeline_id: pipelineId }
      if (period) params.period = period
      const response = await apiClient.get<ApiResponse<TeamForecastOutput>>('/analytics/forecast/team', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get team forecast')
      return response.data.data
    },
  },

  // Insights
  insights: {
    list: async (params?: {
      category?: string
      pipeline_id?: string
      limit?: number
      offset?: number
    }): Promise<AIInsight[]> => {
      const response = await apiClient.get<ApiResponse<AIInsight[]>>('/analytics/insights', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to list insights')
      return response.data.data
    },

    getSummary: async (): Promise<InsightSummary> => {
      const response = await apiClient.get<ApiResponse<InsightSummary>>('/analytics/insights/summary')
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get insight summary')
      return response.data.data
    },

    getCritical: async (limit?: number): Promise<AIInsight[]> => {
      const params = limit ? { limit } : undefined
      const response = await apiClient.get<ApiResponse<AIInsight[]>>('/analytics/insights/critical', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get critical insights')
      return response.data.data
    },

    generate: async (pipelineId?: string): Promise<AIInsight[]> => {
      const response = await apiClient.post<ApiResponse<AIInsight[]>>('/analytics/insights/generate', {
        pipeline_id: pipelineId,
      })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to generate insights')
      return response.data.data
    },

    dismiss: async (id: string): Promise<void> => {
      const response = await apiClient.post<ApiResponse<null>>(`/analytics/insights/${id}/dismiss`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to dismiss insight')
    },

    completeAction: async (insightId: string, actionId: string): Promise<void> => {
      const response = await apiClient.post<ApiResponse<null>>(
        `/analytics/insights/${insightId}/action/${actionId}/complete`
      )
      if (!response.data.success) throw new Error(response.data.error || 'Failed to complete action')
    },
  },

  // Reports
  reports: {
    list: async (params?: { limit?: number; offset?: number }): Promise<AnalyticsReport[]> => {
      const response = await apiClient.get<ApiResponse<AnalyticsReport[]>>('/analytics/reports', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to list reports')
      return response.data.data
    },

    getRecent: async (limit?: number): Promise<AnalyticsReport[]> => {
      const params = limit ? { limit } : undefined
      const response = await apiClient.get<ApiResponse<AnalyticsReport[]>>('/analytics/reports/recent', { params })
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get recent reports')
      return response.data.data
    },

    get: async (id: string): Promise<AnalyticsReport> => {
      const response = await apiClient.get<ApiResponse<AnalyticsReport>>(`/analytics/reports/${id}`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get report')
      return response.data.data
    },

    create: async (data: {
      name: string
      type: 'pipeline' | 'sales' | 'activity' | 'forecast' | 'custom'
      format: 'pdf' | 'csv' | 'xlsx'
      parameters?: Record<string, unknown>
      date_from?: string
      date_to?: string
    }): Promise<AnalyticsReport> => {
      const response = await apiClient.post<ApiResponse<AnalyticsReport>>('/analytics/reports', data)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to create report')
      return response.data.data
    },

    getDownloadUrl: async (id: string): Promise<string> => {
      const response = await apiClient.get<ApiResponse<{ download_url: string }>>(`/analytics/reports/${id}/download`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to get download URL')
      return response.data.data.download_url
    },

    delete: async (id: string): Promise<void> => {
      const response = await apiClient.delete<ApiResponse<null>>(`/analytics/reports/${id}`)
      if (!response.data.success) throw new Error(response.data.error || 'Failed to delete report')
    },
  },
}

// Appointment Booking API
export interface BookingSchedule {
  ID: string
  Name: string
  Timezone: string
  Rules: unknown
  DateOverrides: unknown
  IsDefault: boolean
}

export interface BookingEventType {
  ID: string
  Slug: string
  Title: string
  Description: string | null
  DurationMin: number
  BufferBeforeMin: number
  BufferAfterMin: number
  MinNoticeMin: number
  BookingHorizonDays: number
  SlotIntervalMin: number
  DailyBookingCap: number | null
  LocationKind: string
  ConfirmationMode: string
  ScheduleID: string | null
  IsActive: boolean
}

export interface BookingItem {
  ID: string
  GuestName: string
  GuestEmail: string
  StartUTC: string
  EndUTC: string
  Status: string
}

export interface PublicSlot {
  start: string
  end: string
}

export const bookingApi = {
  listSchedules: async (): Promise<BookingSchedule[]> => {
    const response = await apiClient.get<ApiResponse<BookingSchedule[]>>('/booking/schedules')
    return response.data.data ?? []
  },
  createSchedule: async (data: Record<string, unknown>): Promise<BookingSchedule> => {
    const response = await apiClient.post<ApiResponse<BookingSchedule>>('/booking/schedules', data)
    return response.data.data
  },
  listEventTypes: async (): Promise<BookingEventType[]> => {
    const response = await apiClient.get<ApiResponse<BookingEventType[]>>('/booking/event-types')
    return response.data.data ?? []
  },
  createEventType: async (data: Record<string, unknown>): Promise<BookingEventType> => {
    const response = await apiClient.post<ApiResponse<BookingEventType>>('/booking/event-types', data)
    return response.data.data
  },
  updateEventType: async (id: string, data: Record<string, unknown>): Promise<BookingEventType> => {
    const response = await apiClient.put<ApiResponse<BookingEventType>>(`/booking/event-types/${id}`, data)
    return response.data.data
  },
  deleteEventType: async (id: string): Promise<void> => {
    await apiClient.delete(`/booking/event-types/${id}`)
  },
  listBookings: async (): Promise<BookingItem[]> => {
    const response = await apiClient.get<ApiResponse<BookingItem[]>>('/booking/bookings')
    return response.data.data ?? []
  },
}

export const publicBookingApi = {
  config: async (eventTypeId: string) => {
    const response = await apiClient.get<ApiResponse<{ id: string; title: string; description?: string; duration_min: number; location_kind: string }>>(
      `/public/booking/${eventTypeId}`
    )
    return response.data.data
  },
  slots: async (eventTypeId: string, from: string, to: string): Promise<PublicSlot[]> => {
    const response = await apiClient.get<ApiResponse<{ slots: PublicSlot[] }>>(
      `/public/booking/${eventTypeId}/slots`, { params: { from, to } }
    )
    return response.data.data?.slots ?? []
  },
  book: async (eventTypeId: string, payload: Record<string, unknown>, idempotencyKey: string) => {
    const response = await apiClient.post<ApiResponse<{ booking_id: string; status: string; start: string; end: string; cancel_token: string }>>(
      `/public/booking/${eventTypeId}/book`, payload, { headers: { 'Idempotency-Key': idempotencyKey } }
    )
    return response.data.data
  },
  cancel: async (token: string) => {
    const response = await apiClient.post<ApiResponse<{ status: string }>>(`/public/booking/manage/${token}/cancel`)
    return response.data.data
  },
}
